import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  opsApi, apiError, fmtNum, GRAD, FONT_HEAD, StatusPill, KIT_STATUS, HEALTH_STATUS,
  Pagination, ROLE_RANK, INDIA_ZONES,
} from './opsApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Cpu, Plus, Search, PackageCheck, PackageX, HeartPulse } from 'lucide-react';
import Loader from '../../components/Loader';

export default function OpsFleet({ user }) {
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [allocKit, setAllocKit] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [allocTarget, setAllocTarget] = useState('');
  const [allocSerial, setAllocSerial] = useState('');
  const [busy, setBusy] = useState(false);
  const [addForm, setAddForm] = useState({ model: 'Desky Classroom Kit v2', quantity: 1 });
  const [regScope, setRegScope] = useState('my_scope'); // 'my_scope' or zone slug like 'west'
  const rank = ROLE_RANK[user.role] || 0;

  const userScope = (user?.scope_path || '/india').toLowerCase();
  const userZoneSlug = (() => {
    const parts = userScope.replace(/^\/+|\/+$/g, '').split('/');
    return parts[1] || '';
  })();
  const userZoneName = userZoneSlug ? userZoneSlug[0].toUpperCase() + userZoneSlug.slice(1) : '';
  const availableZones = (() => {
    if (rank >= 5) return INDIA_ZONES; // super admin: all zones
    if (rank >= 4 && userZoneName) return [userZoneName]; // zone admin: own zone
    return []; // state manager registers within own scope only
  })();

  const load = useCallback(() => {
    opsApi.get('/fleet/summary').then((r) => setSummary(r.data));
    opsApi.get('/kits', { params: { page, page_size: 25, status: statusFilter || undefined, search: search || undefined } })
      .then((r) => setData(r.data));
  }, [page, statusFilter, search]);

  useEffect(() => { const t = setTimeout(load, search ? 350 : 0); return () => clearTimeout(t); }, [load, search]);

  const openAllocate = (kit) => {
    setAllocKit(kit);
    setAllocTarget('');
    setAllocSerial('');
    opsApi.get('/institutions', { params: { page_size: 100, stage: 'active' } })
      .then((r) => setInstitutions(r.data.items));
  };

  const run = async (fn, success) => {
    setBusy(true);
    try { await fn(); toast.success(success); setShowAdd(false); setAllocKit(null); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="ops-fleet-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={FONT_HEAD}>Hardware Fleet</h1>
          <p className="text-sm text-slate-500 mt-1">Kit inventory, allocation by zone/institution &amp; device health</p>
        </div>
        {rank >= 2 && (
          <Button data-testid="register-kits-btn" onClick={() => setShowAdd(true)} className="text-white rounded-xl h-10 border-0" style={{ background: GRAD }}>
            <Plus className="w-4 h-4 mr-1.5" /> Register kits
          </Button>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Cpu, label: 'Total kits', value: summary.total },
            { icon: PackageCheck, label: 'Deployed', value: summary.by_status.allocated },
            { icon: PackageX, label: 'In stock', value: summary.by_status.in_stock },
            { icon: HeartPulse, label: 'Faulty / degraded', value: (summary.by_health.faulty || 0) + (summary.by_health.degraded || 0) },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`fleet-kpi-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <div className="h-1" style={{ background: GRAD }} />
              <div className="p-5">
                <div className="flex items-center gap-2 text-xs text-slate-400 uppercase tracking-wide mb-1.5"><Icon className="w-3.5 h-3.5 text-[#34A853]" />{label}</div>
                <div className="text-2xl font-semibold tabular-nums" style={FONT_HEAD}>{fmtNum(value)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="kit-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search serial, model, institution…" className="pl-9 w-72 h-9 bg-white" />
        </div>
        <div className="flex gap-1.5">
          {['', 'in_stock', 'allocated', 'faulty', 'retired'].map((s) => (
            <button key={s || 'all'} data-testid={`kit-filter-${s || 'all'}`} onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border capitalize transition-colors ${
                statusFilter === s ? 'text-white border-transparent' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
              style={statusFilter === s ? { background: GRAD } : undefined}>
              {s ? s.replace('_', ' ') : 'All'}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {!data ? (
          <Loader />
        ) : data.items.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Cpu className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No kits found. Register inventory to start allocating.</p>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs text-slate-400 uppercase tracking-wide">
                  <th className="px-5 py-3 font-medium">Serial</th>
                  <th className="px-4 py-3 font-medium">Model</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Health</th>
                  <th className="px-4 py-3 font-medium">Institution</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((k) => (
                  <tr key={k.id} data-testid={`kit-row-${k.serial_number}`} className="border-b border-slate-50">
                    <td className="px-5 py-3.5 font-mono text-xs font-medium">{k.serial_number}</td>
                    <td className="px-4 py-3.5 text-xs text-slate-500">{k.model}</td>
                    <td className="px-4 py-3.5"><StatusPill value={k.status} map={KIT_STATUS} /></td>
                    <td className="px-4 py-3.5"><StatusPill value={k.health} map={HEALTH_STATUS} /></td>
                    <td className="px-4 py-3.5 text-xs text-slate-600">{k.institution_name || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3.5 text-right">
                      <div className="flex justify-end gap-1.5">
                        {k.status === 'in_stock' && (
                          <Button size="sm" data-testid={`allocate-kit-${k.serial_number}`} onClick={() => openAllocate(k)}
                            className="h-7 text-xs rounded-lg text-white border-0" style={{ background: GRAD }}>Allocate</Button>
                        )}
                        {k.status === 'allocated' && (
                          <Button size="sm" variant="outline" disabled={busy}
                            onClick={() => run(() => opsApi.post(`/kits/${k.id}/unallocate`), 'Kit returned to stock')}
                            className="h-7 text-xs rounded-lg">Return</Button>
                        )}
                        {k.health !== 'faulty' && k.status !== 'retired' && (
                          <Button size="sm" variant="outline" disabled={busy}
                            onClick={() => run(() => opsApi.post(`/kits/${k.id}/status`, { health: 'faulty', status: 'faulty' }), 'Marked faulty')}
                            className="h-7 text-xs rounded-lg text-red-600 border-red-200">Faulty</Button>
                        )}
                        {(k.health === 'faulty' || k.status === 'faulty') && k.status !== 'retired' && (
                          <Button size="sm" variant="outline" disabled={busy}
                            onClick={() => run(() => opsApi.post(`/kits/${k.id}/status`, { health: 'healthy', status: 'in_stock' }), 'Kit repaired & back in stock')}
                            className="h-7 text-xs rounded-lg text-green-700 border-green-200">Repaired</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination data={data} onPage={setPage} />
          </>
        )}
      </div>

      {/* Register kits */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={FONT_HEAD}>Register hardware kits</DialogTitle>
            <DialogDescription>Bulk-registers kits with auto-generated serial numbers under your scope.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Model *</Label>
              <Input data-testid="kit-model-input" value={addForm.model} onChange={(e) => setAddForm((f) => ({ ...f, model: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Registration scope</Label>
              <Select value={regScope} onValueChange={setRegScope}>
                <SelectTrigger data-testid="kit-scope-select"><SelectValue placeholder="Select scope" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="my_scope">My scope ({userScope})</SelectItem>
                  {availableZones.map((z) => (
                    <SelectItem key={z} value={z.toLowerCase()}>Zone — {z}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Quantity (1–1000)</Label>
              <Input data-testid="kit-quantity-input" type="number" min="1" max="1000" value={addForm.quantity}
                onChange={(e) => setAddForm((f) => ({ ...f, quantity: e.target.value }))} className="mt-1" />
            </div>
            <Button data-testid="kit-register-submit" disabled={busy || !addForm.model.trim()}
              onClick={() => {
                const payload = { model: addForm.model, quantity: Number(addForm.quantity) || 1 };
                if (regScope !== 'my_scope') payload.scope_path = `/india/${regScope}`;
                return run(() => opsApi.post('/kits/bulk', payload), `${addForm.quantity} kit(s) registered`);
              }}
              className="w-full text-white rounded-xl border-0" style={{ background: GRAD }}>
              {busy ? 'Registering…' : 'Register kits'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Allocate */}
      <Dialog open={!!allocKit} onOpenChange={(o) => !o && setAllocKit(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={FONT_HEAD}>Allocate {allocKit?.serial_number}</DialogTitle>
            <DialogDescription>Assign this kit to an active institution in your scope.</DialogDescription>
          </DialogHeader>
          <Select value={allocTarget} onValueChange={setAllocTarget}>
            <SelectTrigger data-testid="allocate-institution-select"><SelectValue placeholder="Select institution" /></SelectTrigger>
            <SelectContent>
              {institutions.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}{i.city ? ` — ${i.city}` : ''}</SelectItem>)}
            </SelectContent>
          </Select>
          <div>
            <Label className="text-xs">Serial number *</Label>
            <Input data-testid="allocate-serial-input" placeholder="Enter device serial"
              value={allocSerial} onChange={(e) => setAllocSerial(e.target.value)} className="mt-1" />
          </div>
          <Button data-testid="allocate-submit" disabled={busy || !allocTarget}
            onClick={() => run(() => opsApi.post(`/kits/${allocKit.id}/allocate`, { institution_id: allocTarget, serial_number: allocSerial }), 'Kit allocated')}
            className="w-full text-white rounded-xl border-0" style={{ background: GRAD }}>
            {busy ? 'Allocating…' : 'Allocate kit'}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
